import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { catchError, map, Observable, of } from "rxjs";
import { environment } from "../../../../environments/environment";
import { ApiErrorHandlerService } from "../../utils/api-error-handler/api-error-handler.service";
import { PermissionService } from "../permission/permission.service";
import { ServiceContract, ServiceContractObject } from "../../../models/api/service/serviceContract";
import { Service, ServiceObject } from "../../../models/api/service/serviceHistory";
import { Media } from "../../../models/api/media";

@Injectable({
  providedIn: "root",
})
export class ServiceContractService {
  private http = inject(HttpClient);
  private apiErrorHandler = inject(ApiErrorHandlerService);
  private permissions = inject(PermissionService);

  /**
   * Creates a new service contract.
   * @param serviceContract The service contract to create.
   * @returns An observable of the created service contract.
   */
  public createServiceContract(serviceContract: ServiceContract) {
    const body = serviceContract.values;
    return this.http
      .post(environment.baseUrl + "/serviceContract", body, {
        observe: "response",
      })
      .pipe(this.apiErrorHandler.handle("service_contract"));
  }

  /**
   * Updates a service contract. If the user is not allowed to update external
   * service contracts, the customer is removed from the request body.
   * @param serviceContract The service contract to update.
   * @returns An observable of the updated service contract.
   */
  public editServiceContract(serviceContract: ServiceContract) {
    const body = serviceContract.values;
    if (this.permissions.missesPermission(this.permissions.allPermissions.EXT_SERVICE_CONTRACT_UPDATE)) {
      delete body.customer;
    }
    return this.http
      .put(`${environment.baseUrl}/serviceContract/${serviceContract.id}`, body, {
        observe: "response",
      })
      .pipe(this.apiErrorHandler.handle("service_contract"));
  }

  public getServiceAttachments(serviceId: string) {
    return this.http
      .get<Media[]>(`${environment.baseUrl}/service/${serviceId}/attachments`)
      .pipe(this.apiErrorHandler.handle("file"));
  }

  public getAuthenticatedAttachment(serviceId: string, mediaId: string): Observable<Blob | null> {
    return this.http
      .get(`${environment.baseUrl}/service/${serviceId}/attachments/${mediaId}`, {
        responseType: "blob",
      })
      .pipe(catchError(() => of(null)));
  }

  public addServiceAttachment(serviceId: string, file: File | null, label: string) {
    const formData = new FormData();
    if (file) formData.append("file", file);
    if (label) formData.append("label", label);

    return this.http
      .post<Media[]>(`${environment.baseUrl}/service/${serviceId}/attachments`, formData)
      .pipe(this.apiErrorHandler.handle("file"));
  }

  public deleteServiceAttachement(serviceId: string, mediaId: string) {
    return this.http
      .delete(`${environment.baseUrl}/service/${serviceId}/attachments/${mediaId}`)
      .pipe(this.apiErrorHandler.handle("file"));
  }

  /**
   * Deletes a service contract with the given ID.
   * @param serviceContractId The ID of the service contract to delete.
   * @returns An observable of the HTTP response.
   */
  public deleteServiceContract(serviceContractId: string | undefined) {
    return this.http
      .delete(`${environment.baseUrl}/serviceContract/${serviceContractId}`)
      .pipe(this.apiErrorHandler.handle("service_contract"));
  }
  /**
   * Fetches all service contracts from the API endpoint, converts them into ServiceContract
   * instances, and sorts them by contract name.
   * @returns An observable of a sorted array of ServiceContract instances.
   */
  public getServiceContracts() {
    return this.http.get<ServiceContractObject[]>(environment.baseUrl + "/serviceContract").pipe(
      this.apiErrorHandler.handle("service_contract"),
      map((res) => {
        const serviceContracts = res.map((serviceContract: ServiceContractObject) => {
          return new ServiceContract(serviceContract);
        });
        return this.sortByContractName(serviceContracts);
      })
    );
  }

  /**
   * Sorts an array of ServiceContract instances by their contract name.
   * If one or both of the contracts do not have a contract name, the
   * function assumes they are equal and does not change their order.
   * @param serviceContracts The array of ServiceContract instances to sort.
   * @returns The sorted array.
   */
  private sortByContractName(serviceContracts: ServiceContract[]) {
    return serviceContracts.sort((a, b) => {
      if (a.contractName && b.contractName) {
        return a.contractName.localeCompare(b.contractName);
      } else {
        return 0;
      }
    });
  }

  /**
   * Fetches all services from the API endpoint for a given service contract.
   * The HTTP request is configured to return an empty array on failure.
   * @param serviceContractId The id of the service contract to fetch services for.
   * @returns An observable of an array of Service instances.
   */
  public getServices(serviceContractId: string): Observable<Service[]> {
    return this.http
      .get<ServiceObject[]>(`${environment.baseUrl}/service`, {
        params: {
          serviceContractId,
        },
        observe: "response",
      })
      .pipe(
        this.apiErrorHandler.handle("service_history"),
        map((res) => {
          if (res.body) {
            return res.body?.map((service) => {
              return new Service(service);
            });
          } else {
            return [];
          }
        })
      );
  }

  /**
   * Sends a PUT request to update an existing service.
   * @param serviceId The ID of the service to be updated.
   * @param serviceEditValues The updated values for the service.
   * @returns An observable of the updated service.
   */
  public editService(serviceId: string, serviceEditValues: ServiceObject) {
    return this.http
      .put<Service>(`${environment.baseUrl}/service/${serviceId}`, serviceEditValues)
      .pipe(this.apiErrorHandler.handle("service_history"));
  }
}
